import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router';
import { fetchPrescription, updateAllPrescriptions, fetchMedicationNames, createMedicationName } from '../../../Redux/PrescriptionSlice';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectRoles } from '../../../Redux/AuthSlice';
import { hasPermission } from '../../../Redux/permissionSlice';
import Breadcrumb from '../../dataUi/Breadcrumb';
import { BsPencil, BsX } from 'react-icons/bs';
import { InfinitySpin } from 'react-loader-spinner';
import RxIcon from '../../../assets/rx-logo.png'

const ViewEditPrescription = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { prescriptionDetail, loading: pageLoading } = useSelector((state) => state.prescriptions);
    const { prescriptionId } = useParams();

    // Get required roles from location state or use default
    const requiredRoles = location.state?.requiredRoles || ['prescriptionViewer', 'prescriptionWriter', 'prescriptionAdmin'];
    const selectedRoles = useSelector(selectRoles);
    const roles = Array.isArray(selectedRoles) ? selectedRoles : selectedRoles.split(',').map(role => role.trim());
    const hasRequiredPermissions = hasPermission(roles, requiredRoles);

    const [isPatientEditing, setIsPatientEditing] = useState(false);
    const [isSaving, setIsSaving] = useState({
        patient: false,
        provider: false,
        medication: false,
        all: false
    });
    const [patient, setPatient] = useState({
        firstName: "",
        lastName: "",
        dateOfBirth: "",
        idType: "",
        idNumber: "",
        gender: "",
        phoneNumber: "",
        address: "",
    });

    const [isProviderEditing, setIsProviderEditing] = useState(false);
    const [provider, setProvider] = useState({
        firstName: "",
        lastName: "",
        title: "",
        providerId: "",
        phoneNumber: "",
        facility: "",
    });

    const [isMedicationEditing, setIsMedicationEditing] = useState(false);
    const [medications, setMedications] = useState([]);
    const [editedStatus, setEditedStatus] = useState('');
    const [statusDate, setStatusDate] = useState(null);
    const [prescriptionComment, setPrescriptionComment] = useState('');
    const [prescriptionComments, setPrescriptionComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const { medicationNames } = useSelector((state) => state.prescriptions);
    const ID_TYPES = [
        { value: "passport", label: "Passport" },
        { value: "drivers_license", label: "Driver's License" },
        { value: "ghana_card", label: "Ghana Card" },
        { value: "nhis", label: "NHIS" },
        { value: "state_id", label: "State ID" },
        { value: "Business Card", label: "Business Card" },
        { value: "national_id", label: "National ID" },
        { value: "voter_id", label: "Voter ID" },
        { value: "other", label: "Other" }
    ];

    const GENDER_OPTIONS = [
        { value: "male", label: "Male" },
        { value: "female", label: "Female" },
    ];

    const isPickedUp = prescriptionDetail?.status === 'picked_up';

    // Fetch prescription data
    useEffect(() => {
        if (prescriptionId) {
            dispatch(fetchPrescription(prescriptionId))
                .unwrap()
                .catch((error) => {
                    console.error('Error fetching prescription:', error);
                    toast.error('Failed to fetch prescription details');
                });
        }
    }, [dispatch, prescriptionId]);

    // Update patient state when prescriptionDetail changes
    useEffect(() => {
        if (prescriptionDetail?.patient) {
            setPatient({
                firstName: prescriptionDetail.patient.first_name || "",
                lastName: prescriptionDetail.patient.last_name || "",
                dateOfBirth: prescriptionDetail.patient.date_of_birth || "",
                idType: prescriptionDetail.patient.id_type || "",
                idNumber: prescriptionDetail.patient.id_number || "",
                gender: prescriptionDetail.patient.gender || "",
                phoneNumber: prescriptionDetail.patient.phone_number || "",
                address: prescriptionDetail.patient.address || "",
            });
        }
    }, [prescriptionDetail]);

    // Update provider state when prescriptionDetail changes
    useEffect(() => {
        if (prescriptionDetail?.provider) {
            setProvider({
                firstName: prescriptionDetail.provider.first_name || "",
                lastName: prescriptionDetail.provider.last_name || "",
                title: prescriptionDetail.provider.title || "",
                providerId: prescriptionDetail.provider.provider_id || "",
                phoneNumber: prescriptionDetail.provider.phone_number || "",
                facility: prescriptionDetail.provider.facility || "",
            });
        }
    }, [prescriptionDetail]);

    // Fetch medication names
    useEffect(() => {
        dispatch(fetchMedicationNames())
            .unwrap()
            .then((data) => {
                console.log("Medication names data received:", data);
            })
            .catch((error) => {
                console.error("Error fetching medication names:", error.message || "Unknown error");
            });
    }, [dispatch]);

    // Update medications state when prescriptionDetail changes
    useEffect(() => {
        if (prescriptionDetail?.medications) {
            setMedications(prescriptionDetail.medications.map(med => ({
                medication_name_id: med.medication_name_id || "",
                custom_medication_name: med.name || med.medication_name || "",
                showMedicationDropdown: false,
                dosage_form: med.dosage_form || "",
                strength: med.strength || "",
                route: med.route || "",
                frequency: med.frequency || "",
                quantity: med.quantity || 0,
                unit: med.unit || "",
                refills: med.refills || 0,
                instructions: med.instructions || "",
            })));
        }
    }, [prescriptionDetail]);

    // Update editedStatus when prescriptionDetail changes
    useEffect(() => {
        if (prescriptionDetail?.status) {
            setEditedStatus(prescriptionDetail.status);
            // Set initial status date based on status
            if (prescriptionDetail.status === 'cancelled' && prescriptionDetail.cancelled_at) {
                setStatusDate(prescriptionDetail.cancelled_at);
            } else if (prescriptionDetail.status === 'dispensed' && prescriptionDetail.dispensed_at) {
                setStatusDate(prescriptionDetail.dispensed_at);
            } else {
                setStatusDate(prescriptionDetail.date_issued);
            }
        }
    }, [prescriptionDetail]);

    // Update prescriptionComments when prescriptionDetail changes
    useEffect(() => {
        if (prescriptionDetail?.prescription_comments) {
            setPrescriptionComments(prescriptionDetail.prescription_comments);
            // Format existing comments for display
            const formattedComments = prescriptionDetail.prescription_comments
                .map(comment => {
                    const date = new Date(comment.created_at);
                    const formattedDate = date.toLocaleString('', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: true
                    });
                    return `${formattedDate}: ${comment.content}`;
                })
                .join('\n\n');
            setPrescriptionComment(formattedComments);
        }
    }, [prescriptionDetail]);

    const handlePatientInfoChange = (field, value) => {
        setPatient(prev => ({ ...prev, [field]: value }));
    };

    const handlePatientSubmit = async (e) => {
        e?.preventDefault();
        try {
            setIsSaving(prev => ({ ...prev, patient: true }));
            if (!prescriptionDetail?.id) {
                toast.error('Prescription ID is missing');
                return;
            }

            if (!prescriptionDetail?.patient?.id) {
                toast.error('Patient ID is missing');
                return;
            }

            const updateData = {
                patient: {
                    first_name: patient.firstName,
                    last_name: patient.lastName,
                    date_of_birth: patient.dateOfBirth,
                    id_type: patient.idType,
                    id_number: patient.idNumber,
                    gender: patient.gender,
                    phone_number: patient.phoneNumber,
                    address: patient.address,
                }
            };

            console.log('Updating patient with data:', updateData);

            const result = await dispatch(updateAllPrescriptions({
                prescriptionIds: [prescriptionDetail.id],
                prescriptionData: updateData
            })).unwrap();

            console.log('Update result:', result);

            // Refresh the prescription data after update
            await dispatch(fetchPrescription(prescriptionId)).unwrap();

            setIsPatientEditing(false);
            toast.success("Patient details updated successfully");
        } catch (error) {
            console.error("Error updating patient details:", error);
            toast.error(error.message || "Failed to update patient details");
        } finally {
            setIsSaving(prev => ({ ...prev, patient: false }));
        }
    };

    const handleProviderInfoChange = (field, value) => {
        setProvider(prev => ({ ...prev, [field]: value }));
    };

    const handleProviderSubmit = async (e) => {
        e?.preventDefault();
        try {
            setIsSaving(prev => ({ ...prev, provider: true }));
            if (!prescriptionDetail?.id) {
                toast.error('Prescription ID is missing');
                return;
            }

            const updateData = {
                provider: {
                    first_name: provider.firstName,
                    last_name: provider.lastName,
                    title: provider.title,
                    provider_id: provider.providerId,
                    phone_number: provider.phoneNumber,
                    facility: provider.facility,
                }
            };

            console.log('Updating provider with data:', updateData);

            const result = await dispatch(updateAllPrescriptions({
                prescriptionIds: [prescriptionDetail.id],
                prescriptionData: updateData
            })).unwrap();

            console.log('Update result:', result);

            // Refresh the prescription data after update
            await dispatch(fetchPrescription(prescriptionId)).unwrap();

            setIsProviderEditing(false);
            toast.success("Provider details updated successfully");
        } catch (error) {
            console.error("Error updating provider details:", error);
            toast.error(error.message || "Failed to update provider details");
        } finally {
            setIsSaving(prev => ({ ...prev, provider: false }));
        }
    };

    // const handleMedicationChange = (index, field, value) => {
    //     const updatedMedications = [...medications];
    //     if (field === "quantity" || field === "refills") {
    //         updatedMedications[index][field] = value === "" ? 0 : Number(value);
    //     } else if (field === "strength") {
    //         updatedMedications[index][field] = value.toString();
    //     } else {
    //         updatedMedications[index][field] = value;
    //     }
    //     setMedications(updatedMedications);
    // };
    const handleMedicationChange = (index, field, value) => {
        const updatedMedications = [...medications];

        if (field === "custom_medication_name") {
            const existingMed = updatedMedications[index];
            updatedMedications[index] = {
                ...existingMed, // keep existing fields like medication_name_id, instructions etc.
                custom_medication_name: value,
                dosage_form: "",
                strength: "",
                unit: "",
                route: "",
                frequency: "",
                quantity: 0,
                refills: 0
            };
        } else {
            if (field === "quantity" || field === "refills") {
                updatedMedications[index][field] = value === "" ? 0 : Number(value);
            } else if (field === "strength") {
                updatedMedications[index][field] = value.toString();
            } else {
                updatedMedications[index][field] = value;
            }
        }

        setMedications(updatedMedications);
    };




    const handleMedicationSubmit = async (e) => {
        e?.preventDefault();
        try {
            setIsSaving(prev => ({ ...prev, medication: true }));
            if (!prescriptionDetail?.id) {
                toast.error('Prescription ID is missing');
                return;
            }

            const updateData = {
                medications: medications.map(med => ({
                    medication_name_id: Number(med.medication_name_id),
                    strength: String(med.strength),
                    quantity: Number(med.quantity),
                    refills: Number(med.refills || 0),
                    instructions: med.instructions || "Take as prescribed",
                    unit: med.unit || "Tablet",
                    dosage_form: med.dosage_form || "Tablet",
                    route: med.route || "Oral",
                    frequency: med.frequency || "Once daily"
                }))
            };

            console.log('Updating medications with data:', updateData);

            const result = await dispatch(updateAllPrescriptions({
                prescriptionIds: [prescriptionDetail.id],
                prescriptionData: updateData
            })).unwrap();

            console.log('Update result:', result);

            // Refresh the prescription data after update
            await dispatch(fetchPrescription(prescriptionId)).unwrap();

            setIsMedicationEditing(false);
            toast.success("Medications updated successfully");
        } catch (error) {
            console.error("Error updating medications:", error);
            toast.error(error.message || "Failed to update medications");
        } finally {
            setIsSaving(prev => ({ ...prev, medication: false }));
        }
    };

    const handleStatusChange = (e) => {
        const newStatus = e.target.value;
        setEditedStatus(newStatus);
        // Always use date_issued from database
        setStatusDate(prescriptionDetail.date_issued);
    };

    const handleSaveAll = async (e) => {
        e?.preventDefault();
        try {
            setIsSaving(prev => ({ ...prev, all: true }));

            // Format current date to YYYY-MM-DD only
            const now = new Date();
            const currentDate = now.toISOString().split('T')[0];

            // Prepare the update data with status and date
            const updateData = {
                status: editedStatus,
                date_issued: currentDate
            };

            // Add comments if any
            if (newComment) {
                updateData.prescription_comments = [{
                    content: newComment,
                    user_id: prescriptionDetail.user?.id,
                    branch_id: prescriptionDetail.branch_id
                }];
            }

            console.log('Saving prescription with data:', updateData);

            // Save all changes using updateAllPrescriptions
            const result = await dispatch(updateAllPrescriptions({
                prescriptionIds: [prescriptionDetail.id],
                prescriptionData: updateData
            })).unwrap();

            console.log('Update result:', result);

            // Refresh the prescription data
            await dispatch(fetchPrescription(prescriptionId));

            // Update local state with the new status and date
            setEditedStatus(result.data.status);
            setStatusDate(result.data.date_issued);

            setNewComment(''); // Clear the new comment input
            toast.success('Prescription updated successfully');
        } catch (error) {
            console.error('Error saving changes:', error);
            toast.error('Failed to save changes');
        } finally {
            setIsSaving(prev => ({ ...prev, all: false }));
        }
    };

    const breadcrumbItems = [
        {
            label: "Prescription",
            onClick: () => navigate(-1),
        },
        {
            label: "Prescription Order",
        },
    ];

    return (
        <div className="w-full font-lufga">
            {pageLoading && (
                <div className="absolute top-0 left-0 flex items-center justify-center w-full h-full bg-gradient-to-br from-opacity-40 to-opacity-60 backdrop-blur-sm">
                    <InfinitySpin color="#059212" loading={pageLoading} size={40} />
                </div>
            )}
            {!pageLoading && (
                <div className="mx-6">
                    <div className="mb-2 space-y-1">
                        <p className="text-2xl font-semibold text-slate-600">
                            Prescription Overview
                        </p>
                        <Breadcrumb
                            items={breadcrumbItems}
                            className="flex items-center text-sm text-green-500 transition-all duration-300 hover:text-green-600"
                        />
                    </div>

                    {/* Add the oder id here */}
                    <div className="flex items-center justify-center p-3 mx-auto mb-4 bg-green-100 rounded-lg shadow w-fit">
                        <div className="flex items-center justify-center w-fit">
                            <img src={RxIcon} alt="Rx Logo" width={30} />
                            <div className='flex items-center gap-2 border-b-2 border-green-700'>
                                <h2 className="text-lg font-semibold text-green-700">Order ID:</h2>
                                <p className="text-gray-700">{prescriptionDetail.order_id || "N/A"}</p>
                            </div>

                        </div>
                    </div>

                    <div className="p-3 mb-4 border-b rounded-md shadow">
                        <div className="flex items-center justify-between mb-2">
                            <h2 className="mb-2 text-lg font-semibold text-green-700">Patient Info</h2>
                            {hasRequiredPermissions && (
                                <button
                                    onClick={() => setIsPatientEditing(!isPatientEditing)}
                                    disabled={isPickedUp}
                                    className={`${isPatientEditing ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'} text-white p-1 px-1.5 rounded-md text-sm shadow-lg hover:shadow-xl transition-all duration-300 ${isPickedUp ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    {isPatientEditing ? (
                                        <BsX className="inline-block text-sm text-white" />
                                    ) : (
                                        <BsPencil className="inline-block text-sm text-white" />
                                    )} {" "}
                                    {isPatientEditing ? "Cancel" : "Edit"}
                                </button>
                            )}
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div>
                                <label className="block text-sm font-semibold">First Name{isPatientEditing && <span className="text-red-500">*</span>}</label>
                                {isPatientEditing ? (
                                    <input
                                        type="text"
                                        value={patient.firstName}
                                        onChange={(e) => handlePatientInfoChange("firstName", e.target.value)}
                                        className="w-full p-2 border rounded h-[32px]"
                                    />
                                ) : (
                                    <p className="text-gray-700">{patient.firstName || "N/A"}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold">Last Name{isPatientEditing && <span className="text-red-500">*</span>}</label>
                                {isPatientEditing ? (
                                    <input
                                        type="text"
                                        value={patient.lastName}
                                        onChange={(e) => handlePatientInfoChange("lastName", e.target.value)}
                                        className="w-full p-2 border rounded h-[32px]"
                                    />
                                ) : (
                                    <p className="text-gray-700">{patient.lastName || "N/A"}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold">Date of Birth{isPatientEditing && <span className="text-red-500">*</span>}</label>
                                {isPatientEditing ? (
                                    <input
                                        type="date"
                                        value={patient.dateOfBirth}
                                        onChange={(e) => handlePatientInfoChange("dateOfBirth", e.target.value)}
                                        className="w-full p-2 border rounded h-[32px]"
                                    />
                                ) : (
                                    <p className="text-gray-700">{new Date(patient.dateOfBirth).toLocaleDateString() || "N/A"}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold">Gender{isPatientEditing && <span className="text-red-500">*</span>}</label>
                                {isPatientEditing ? (
                                    <select
                                        value={patient.gender}
                                        onChange={(e) => handlePatientInfoChange("gender", e.target.value)}
                                        className="w-full p-2 border rounded h-[34px]"
                                    >
                                        <option value="">Select Gender</option>
                                        {GENDER_OPTIONS.map((gender) => (
                                            <option key={gender.value} value={gender.value}>
                                                {gender.label}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <p className="text-gray-700">
                                        {GENDER_OPTIONS.find((gender) => gender.value === patient.gender)?.label || "N/A"}
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold">Phone number{isPatientEditing && <span className="text-red-500">*</span>}</label>
                                {isPatientEditing ? (
                                    <input
                                        type="text"
                                        value={patient.phoneNumber}
                                        onChange={(e) => handlePatientInfoChange("phoneNumber", e.target.value)}
                                        className="w-full p-2 border rounded h-[34px]"
                                    />
                                ) : (
                                    <p className="text-gray-700">{patient.phoneNumber || "N/A"}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold">Address{isPatientEditing && <span className="text-red-500">*</span>}</label>
                                {isPatientEditing ? (
                                    <input
                                        type="text"
                                        value={patient.address}
                                        onChange={(e) => handlePatientInfoChange("address", e.target.value)}
                                        className="w-full p-2 border rounded h-[32px]"
                                    />
                                ) : (
                                    <p className="text-gray-700">{patient.address || "N/A"}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold">ID Type{isPatientEditing && <span className="text-red-500">*</span>}</label>
                                {isPatientEditing ? (
                                    <select
                                        value={patient.idType}
                                        onChange={(e) => handlePatientInfoChange("idType", e.target.value)}
                                        className="w-full p-2 border rounded h-[34px]"
                                    >
                                        <option value="">Select ID Type</option>
                                        {ID_TYPES.map((type) => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <p className="text-gray-700">
                                        {ID_TYPES.find((type) => type.value === patient.idType)?.label || patient.idType || "N/A"}
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold">ID Number{isPatientEditing && <span className="text-red-500">*</span>}</label>
                                {isPatientEditing ? (
                                    <input
                                        type="text"
                                        value={patient.idNumber}
                                        onChange={(e) => handlePatientInfoChange("idNumber", e.target.value)}
                                        className="w-full p-2 border rounded h-[32px]"
                                    />
                                ) : (
                                    <p className="text-gray-700">{patient.idNumber || "N/A"}</p>
                                )}
                            </div>
                        </div>
                        {isPatientEditing && (
                            <div className="flex justify-end mt-4">
                                <button
                                    onClick={handlePatientSubmit}
                                    type="button"
                                    disabled={isSaving.patient || isSaving.provider || isSaving.medication || isSaving.all}
                                    className={`bg-green-500 hover:opacity-90 text-white py-1 px-4 rounded-md shadow-sm text-sm ${(isSaving.patient || isSaving.provider || isSaving.medication || isSaving.all) ? 'opacity-50 cursor-not-allowed' : ''
                                        }`}
                                >
                                    {isSaving.patient ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Provider Information Section */}
                    <div className="p-3 mb-4 border-b rounded-md shadow">
                        <div className="flex items-center justify-between mb-2">
                            <h2 className="mb-2 text-lg font-semibold text-green-700">Provider Information</h2>
                            {hasRequiredPermissions && prescriptionDetail?.provider && (
                                <button
                                    onClick={() => setIsProviderEditing(!isProviderEditing)}
                                    disabled={isPickedUp}
                                    className={`${isProviderEditing ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'} text-white p-1 px-1.5 rounded-md text-sm shadow-lg hover:shadow-xl transition-all duration-300 ${isPickedUp ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    {isProviderEditing ? (
                                        <BsX className="inline-block text-sm text-white" />
                                    ) : (
                                        <BsPencil className="inline-block text-sm text-white" />
                                    )} {" "}
                                    {isProviderEditing ? "Cancel" : "Edit"}
                                </button>
                            )}
                        </div>

                        {!prescriptionDetail?.provider ? (
                            <div className="py-8 text-center rounded-lg bg-gray-50">
                                <svg
                                    className="w-12 h-12 mx-auto text-green-500"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                    />
                                </svg>
                                <h3 className="mt-2 text-sm font-medium text-gray-900">Patient-Initiated Prescription</h3>
                                <p className="mt-1 text-sm text-gray-500">This prescription was created by the patient without a healthcare provider.</p>
                                <div className="inline-flex items-center px-3 py-1 mt-4 text-sm font-medium text-green-800 bg-green-100 rounded-full">
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    Self-prescribed
                                </div>
                            </div>
                        ) : (
                            <div className="grid gap-4 sm:grid-cols-2">
                                <div>
                                    <label className="block text-sm font-semibold">First Name{isProviderEditing && <span className="text-red-500">*</span>}</label>
                                    {isProviderEditing ? (
                                        <input
                                            type="text"
                                            value={provider.firstName}
                                            onChange={(e) => handleProviderInfoChange("firstName", e.target.value)}
                                            className="w-full p-2 border rounded h-[32px]"
                                        />
                                    ) : (
                                        <p className="text-gray-700">{provider.firstName || "N/A"}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold">Last Name{isProviderEditing && <span className="text-red-500">*</span>}</label>
                                    {isProviderEditing ? (
                                        <input
                                            type="text"
                                            value={provider.lastName}
                                            onChange={(e) => handleProviderInfoChange("lastName", e.target.value)}
                                            className="w-full p-2 border rounded h-[32px]"
                                        />
                                    ) : (
                                        <p className="text-gray-700">{provider.lastName || "N/A"}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold">Title{isProviderEditing && <span className="text-red-500">*</span>}</label>
                                    {isProviderEditing ? (
                                        <select
                                            value={provider.title}
                                            onChange={(e) => handleProviderInfoChange("title", e.target.value)}
                                            className="w-full p-2 border rounded h-[32px]"
                                        >
                                            <option value="">Select Title</option>
                                            <option value="MD">MD</option>
                                            <option value="DO">DO</option>
                                            <option value="NP">NP</option>
                                            <option value="PA">PA</option>
                                            <option value="RPh">RPh</option>
                                            <option value="PharmD">PharmD</option>
                                        </select>
                                    ) : (
                                        <p className="text-gray-700">{provider.title || "N/A"}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold">Provider ID{isProviderEditing && <span className="text-red-500">*</span>}</label>
                                    {isProviderEditing ? (
                                        <input
                                            type="text"
                                            value={provider.providerId}
                                            onChange={(e) => handleProviderInfoChange("providerId", e.target.value)}
                                            className="w-full p-2 border rounded h-[32px]"
                                        />
                                    ) : (
                                        <p className="text-gray-700">{provider.providerId || "N/A"}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold">Phone Number{isProviderEditing && <span className="text-red-500">*</span>}</label>
                                    {isProviderEditing ? (
                                        <input
                                            type="text"
                                            value={provider.phoneNumber}
                                            onChange={(e) => handleProviderInfoChange("phoneNumber", e.target.value)}
                                            className="w-full p-2 border rounded h-[32px]"
                                        />
                                    ) : (
                                        <p className="text-gray-700">{provider.phoneNumber || "N/A"}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold">Facility{isProviderEditing && <span className="text-red-500">*</span>}</label>
                                    {isProviderEditing ? (
                                        <input
                                            type="text"
                                            value={provider.facility}
                                            onChange={(e) => handleProviderInfoChange("facility", e.target.value)}
                                            className="w-full p-2 border rounded h-[32px]"
                                        />
                                    ) : (
                                        <p className="text-gray-700">{provider.facility || "N/A"}</p>
                                    )}
                                </div>
                            </div>
                        )}
                        {isProviderEditing && (
                            <div className="flex justify-end mt-4">
                                <button
                                    onClick={handleProviderSubmit}
                                    type="button"
                                    disabled={isSaving.patient || isSaving.provider || isSaving.medication || isSaving.all}
                                    className={`bg-green-500 hover:opacity-90 text-white py-1 px-4 rounded-md shadow-sm text-sm ${(isSaving.patient || isSaving.provider || isSaving.medication || isSaving.all) ? 'opacity-50 cursor-not-allowed' : ''
                                        }`}
                                >
                                    {isSaving.provider ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Medications Section */}
                    <div className="p-3 mb-4 border-b rounded-md shadow">
                        <div className="flex items-center justify-between mb-2">
                            <h2 className="mb-2 text-lg font-semibold text-green-700">Prescription Details</h2>
                            {hasRequiredPermissions && medications.length > 0 && (
                                <button
                                    onClick={() => setIsMedicationEditing(!isMedicationEditing)}
                                    disabled={isPickedUp}
                                    className={`${isMedicationEditing ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'} text-white p-1 px-1.5 rounded-md text-sm shadow-lg hover:shadow-xl transition-all duration-300 ${isPickedUp ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    {isMedicationEditing ? (
                                        <BsX className="inline-block text-sm text-white" />
                                    ) : (
                                        <BsPencil className="inline-block text-sm text-white" />
                                    )} {" "}
                                    {isMedicationEditing ? "Cancel" : "Edit"}
                                </button>
                            )}
                        </div>

                        {!medications || medications.length === 0 ? (
                            <div className="py-8 text-center rounded-lg bg-gray-50">
                                <svg
                                    className="w-12 h-12 mx-auto text-green-500"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                                    />
                                </svg>
                                <h3 className="mt-2 text-sm font-medium text-gray-900">No Medications Added</h3>
                                <p className="mt-1 text-sm text-gray-500">This prescription does not have any medications yet.</p>
                                <div className="inline-flex items-center px-3 py-1 mt-4 text-sm font-medium text-green-800 bg-green-100 rounded-full">
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                    Add medications to proceed
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {medications.map((medication, index) => (
                                    <div key={index} className="relative p-4 bg-white border border-green-100 rounded-lg shadow-sm">
                                        <div className="absolute px-3 py-1 text-xs text-white bg-green-500 rounded-full -top-3 left-3">
                                            Medication #{index + 1}
                                        </div>
                                        <div className="flex flex-col gap-4 mt-2">
                                            {/* Medication Name */}
                                            <div className="w-full">
                                                <label className="block text-sm font-semibold">Medication Name{isMedicationEditing && <span className="text-red-500">*</span>}</label>
                                                {isMedicationEditing ? (
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            value={medication.custom_medication_name}
                                                            onChange={(e) => {
                                                                handleMedicationChange(
                                                                    index,
                                                                    "custom_medication_name",
                                                                    e.target.value
                                                                );
                                                                // Show dropdown only when typing
                                                                setMedications(prevMeds => {
                                                                    const updatedMeds = [...prevMeds];
                                                                    updatedMeds[index] = {
                                                                        ...updatedMeds[index],
                                                                        showMedicationDropdown: e.target.value.trim() !== ''
                                                                    };
                                                                    return updatedMeds;
                                                                });
                                                            }}
                                                            onFocus={() => {
                                                                // Only show dropdown if there's text in the input
                                                                if (medication.custom_medication_name && medication.custom_medication_name.trim() !== '') {
                                                                    setMedications(prevMeds => {
                                                                        const updatedMeds = [...prevMeds];
                                                                        updatedMeds[index] = {
                                                                            ...updatedMeds[index],
                                                                            showMedicationDropdown: true
                                                                        };
                                                                        return updatedMeds;
                                                                    });
                                                                }
                                                            }}
                                                            onBlur={() => {
                                                                // Delay hiding dropdown to allow clicking on suggestions
                                                                setTimeout(() => {
                                                                    setMedications(prevMeds => {
                                                                        const updatedMeds = [...prevMeds];
                                                                        updatedMeds[index] = {
                                                                            ...updatedMeds[index],
                                                                            showMedicationDropdown: false
                                                                        };
                                                                        return updatedMeds;
                                                                    });
                                                                }, 200);
                                                            }}
                                                            className="border px-2 h-[32px] rounded w-full focus:border-green-500 focus:ring-green-500 focus:outline-none"
                                                            placeholder="Type to search or add new medication"
                                                        />

                                                        {/* Fixed medication suggestions dropdown that works with direct array format */}
                                                        {medication.showMedicationDropdown && (
                                                            <div className="absolute z-50 w-full mt-1 overflow-y-auto bg-white border rounded shadow-md max-h-60">
                                                                {/* Display status if loading or no data */}
                                                                {pageLoading ? (
                                                                    <div className="px-3 py-2 text-center text-gray-500">
                                                                        Loading medications...
                                                                    </div>
                                                                ) : !medicationNames ||
                                                                    (Array.isArray(medicationNames) && medicationNames.length === 0) ||
                                                                    (medicationNames?.data && medicationNames.data.length === 0) ? (
                                                                    <div className="px-3 py-2 text-center text-gray-500">
                                                                        No medication data available
                                                                    </div>
                                                                ) : (
                                                                    <>
                                                                        {/* Safe rendering of medication options */}
                                                                        {(Array.isArray(medicationNames) ? medicationNames : medicationNames?.data || [])
                                                                            .filter(med => {
                                                                                const searchTerm = String(medication.custom_medication_name || '').toLowerCase();
                                                                                const medName = String(med.name || '').toLowerCase();
                                                                                return medName.startsWith(searchTerm);
                                                                            })
                                                                            .sort((a, b) => {
                                                                                const nameA = String(a.name || '').toLowerCase();
                                                                                const nameB = String(b.name || '').toLowerCase();
                                                                                return nameA.localeCompare(nameB);
                                                                            })
                                                                            .slice(0, 20) // Limit results for performance
                                                                            .map(med => (
                                                                                <div
                                                                                    key={med.id || `med-${Math.random()}`}
                                                                                    className="px-3 py-2 border-b cursor-pointer hover:bg-green-50"
                                                                                    onClick={() => {
                                                                                        // Extract strength from the medication name if available
                                                                                        const strengthMatch = med.name.match(/(\d+(?:\.\d+)?)\s*(?:mg|g|ml|mL)/i);
                                                                                        const strength = strengthMatch ? strengthMatch[1] : '';

                                                                                        // Extract dosage form from the medication name
                                                                                        const dosageFormMatch = med.name.match(/(Tablet|Capsule|Injection|Syrup|Cream|Ointment|Inhaler)/i);
                                                                                        const dosageForm = dosageFormMatch ? dosageFormMatch[1] : '';

                                                                                        handleMedicationChange(index, "medication_name_id", med.id);
                                                                                        handleMedicationChange(index, "custom_medication_name", med.name);
                                                                                        // if (strength) {
                                                                                        //     handleMedicationChange(index, "strength", strength);
                                                                                        // }
                                                                                        // if (dosageForm) {
                                                                                        //     handleMedicationChange(index, "dosage_form", dosageForm);
                                                                                        // }
                                                                                        setMedications(prevMeds => {
                                                                                            const updatedMeds = [...prevMeds];
                                                                                            updatedMeds[index] = {
                                                                                                ...updatedMeds[index],
                                                                                                showMedicationDropdown: false
                                                                                            };
                                                                                            return updatedMeds;
                                                                                        });
                                                                                    }}
                                                                                >
                                                                                    <p className="font-medium">{String(med.name || '')}</p>
                                                                                </div>
                                                                            ))}

                                                                        {/* Show message if no matching medications */}
                                                                        {medication.custom_medication_name &&
                                                                            (Array.isArray(medicationNames) ? medicationNames : medicationNames?.data || [])
                                                                                .filter(med =>
                                                                                    med.name &&
                                                                                    typeof med.name === 'string' &&
                                                                                    med.name.toLowerCase().includes(String(medication.custom_medication_name).toLowerCase())
                                                                                ).length === 0 && (
                                                                                <div className="px-3 py-2 text-center text-gray-500">
                                                                                    No matching medications found
                                                                                </div>
                                                                            )}
                                                                    </>
                                                                )}

                                                                {/* Add new medication option if text is entered */}
                                                                {medication.custom_medication_name && String(medication.custom_medication_name).trim() !== "" && (
                                                                    <div
                                                                        className="px-3 py-2 bg-green-100 border-b cursor-pointer hover:bg-green-50"
                                                                        onClick={async () => {
                                                                            try {
                                                                                // Save new medication name to database
                                                                                const result = await dispatch(createMedicationName({
                                                                                    name: medication.custom_medication_name,
                                                                                    branch_id: prescriptionDetail.branch_id
                                                                                })).unwrap();

                                                                                console.log("Created new medication:", result);

                                                                                // Update the medication with the new ID
                                                                                const newMedicationId = result.id || (result.data && result.data.id);

                                                                                if (!newMedicationId) {
                                                                                    throw new Error("No medication ID received from server");
                                                                                }

                                                                                // Update the medication with the new ID
                                                                                handleMedicationChange(index, "medication_name_id", newMedicationId);

                                                                                // Keep the name for display
                                                                                handleMedicationChange(index, "custom_medication_name", medication.custom_medication_name);

                                                                                toast.success(`Added new medication: ${medication.custom_medication_name}`);

                                                                                // Refresh medication list
                                                                                await dispatch(fetchMedicationNames());

                                                                                // Hide dropdown after selection
                                                                                setMedications(prevMeds => {
                                                                                    const updatedMeds = [...prevMeds];
                                                                                    updatedMeds[index] = {
                                                                                        ...updatedMeds[index],
                                                                                        showMedicationDropdown: false
                                                                                    };
                                                                                    return updatedMeds;
                                                                                });
                                                                            } catch (error) {
                                                                                toast.error(`Failed to add medication: ${error.message || 'Unknown error'}`);
                                                                                console.error("Error creating medication:", error);
                                                                            }
                                                                        }}
                                                                    >
                                                                        <p className="font-medium text-green-700">+ Add "{String(medication.custom_medication_name)}" as new medication</p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <p className="text-gray-700">{medication.custom_medication_name || "N/A"}</p>
                                                )}
                                            </div>

                                            <div className="flex flex-col w-full gap-4 md:flex-row">
                                                {/* Dosage Form */}
                                                <div className="w-full">
                                                    <label className="block text-sm font-semibold">Dosage Form{isMedicationEditing && <span className="text-red-500">*</span>}</label>
                                                    {isMedicationEditing ? (
                                                        <select
                                                            value={medication.dosage_form}
                                                            onChange={(e) => handleMedicationChange(index, "dosage_form", e.target.value)}
                                                            className="border px-2 h-[32px] rounded w-full focus:border-green-500 focus:ring-green-500 focus:outline-none"
                                                        >
                                                            <option value="">Select Dosage Form</option>
                                                            <option value="Tablet">Tablet</option>
                                                            <option value="Capsule">Capsule</option>
                                                            <option value="Lozenge">Lozenge</option>
                                                            <option value="Effervescent">Effervescent</option>
                                                            <option value="Syrup">Syrup</option>
                                                            <option value="Drop">Drop</option>
                                                            <option value="Inhaler">Inhaler</option>
                                                            <option value="Ointment">Ointment</option>
                                                            <option value="Cream">Cream</option>
                                                            <option value="Gel">Gel</option>
                                                            <option value="Paste">Paste</option>
                                                            <option value="Suppository">Suppository</option>
                                                            <option value="Injection">Injection</option>
                                                            <option value="Implant">Implant</option>
                                                            <option value="Powder">Powder</option>
                                                            <option value="Pessary">Pessary</option>
                                                        </select>
                                                    ) : (
                                                        <p className="text-gray-700">{medication.dosage_form || "N/A"}</p>
                                                    )}
                                                </div>

                                                {/* Strength */}
                                                <div className="w-full">
                                                    <label className="block text-sm font-semibold">Strength{isMedicationEditing && <span className="text-red-500">*</span>}</label>
                                                    {isMedicationEditing ? (
                                                        <input
                                                            type="text"
                                                            value={medication.strength}
                                                            onChange={(e) => handleMedicationChange(index, "strength", e.target.value)}
                                                            className="border px-2 h-[32px] rounded w-full focus:border-green-500 focus:ring-green-500 focus:outline-none"
                                                            placeholder="Enter strength (e.g., 500)"
                                                        />
                                                    ) : (
                                                        <p className="text-gray-700">{medication.strength || "N/A"}</p>
                                                    )}
                                                </div>

                                                {/* Unit */}
                                                <div className="w-full">
                                                    <label className="block text-sm font-semibold">Unit{isMedicationEditing && <span className="text-red-500">*</span>}</label>
                                                    {isMedicationEditing ? (
                                                        <select
                                                            value={medication.unit}
                                                            onChange={(e) => handleMedicationChange(index, "unit", e.target.value)}
                                                            className="border px-2 h-[32px] rounded w-full focus:border-green-500 focus:ring-green-500 focus:outline-none"
                                                        >
                                                            <option value="">Select Unit</option>
                                                            <option value="Gram">Gram</option>
                                                            <option value="Milligram">Milligram</option>
                                                            <option value="Microgram">Microgram</option>
                                                            <option value="Milliequivalent">Milliequivalent</option>
                                                            <option value="Liter">Liter</option>
                                                            <option value="Milliliter">Milliliter</option>
                                                            <option value="Teaspoon">Teaspoon</option>
                                                            <option value="Tablespoon">Tablespoon</option>
                                                            <option value="Drop">Drop(s)</option>
                                                        </select>
                                                    ) : (
                                                        <p className="text-gray-700">{medication.unit || "N/A"}</p>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex flex-col w-full gap-4 md:flex-row">
                                                {/* Route */}
                                                <div className="w-full">
                                                    <label className="block text-sm font-semibold">Route{isMedicationEditing && <span className="text-red-500">*</span>}</label>
                                                    {isMedicationEditing ? (
                                                        <select
                                                            value={medication.route}
                                                            onChange={(e) => handleMedicationChange(index, "route", e.target.value)}
                                                            className="border px-2 h-[32px] rounded w-full focus:border-green-500 focus:ring-green-500 focus:outline-none"
                                                        >
                                                            <option value="">Select Route</option>
                                                            <option value="Oral">Oral</option>
                                                            <option value="Sublingual">Sublingual</option>
                                                            <option value="Buccal">Buccal</option>
                                                            <option value="Intravenous">Intravenous</option>
                                                            <option value="Intramuscular">Intramuscular</option>
                                                            <option value="Subcutaneous">Subcutaneous</option>
                                                            <option value="Intrathecal">Intrathecal</option>
                                                            <option value="Topical">Topical</option>
                                                            <option value="Inhalation">Inhalation</option>
                                                            <option value="Rectal">Rectal</option>
                                                            <option value="Intravaginal">Intravaginal</option>
                                                            <option value="Ophthalmic">Ophthalmic</option>
                                                            <option value="Otic">Otic</option>
                                                            <option value="Periodontal">Periodontal</option>
                                                        </select>
                                                    ) : (
                                                        <p className="text-gray-700">{medication.route || "N/A"}</p>
                                                    )}
                                                </div>

                                                {/* Frequency */}
                                                <div className="w-full">
                                                    <label className="block text-sm font-semibold">Frequency{isMedicationEditing && <span className="text-red-500">*</span>}</label>
                                                    {isMedicationEditing ? (
                                                        <select
                                                            value={medication.frequency}
                                                            onChange={(e) => handleMedicationChange(index, "frequency", e.target.value)}
                                                            className="border px-2 h-[32px] rounded w-full focus:border-green-500 focus:ring-green-500 focus:outline-none"
                                                        >
                                                            <option value="">Select Frequency</option>
                                                            <option value="Once daily">Once daily</option>
                                                            <option value="Twice daily">Twice daily</option>
                                                            <option value="Three times daily">Three times daily</option>
                                                            <option value="Four times daily">Four times daily</option>
                                                            <option value="Every other day">Every other day</option>
                                                            <option value="As needed">As needed</option>
                                                            <option value="Once weekly">Once weekly</option>
                                                            <option value="Every 24 hours">Every 24 hours</option>
                                                            <option value="Every 12 hours">Every 12 hours</option>
                                                            <option value="Every 8 hours">Every 8 hours</option>
                                                            <option value="Before meals">Before meals</option>
                                                            <option value="After meals">After meals</option>
                                                            <option value="Every morning">Every morning</option>
                                                            <option value="Every evening">Every evening</option>
                                                            <option value="Every night at bedtime">Every night at bedtime</option>
                                                            <option value="Three times a week">Three times a week</option>
                                                        </select>
                                                    ) : (
                                                        <p className="text-gray-700">{medication.frequency || "N/A"}</p>
                                                    )}
                                                </div>

                                                {/* Quantity */}
                                                <div className="w-full">
                                                    <label className="block text-sm font-semibold">Quantity{isMedicationEditing && <span className="text-red-500">*</span>}</label>
                                                    {isMedicationEditing ? (
                                                        <input
                                                            type="number"
                                                            value={medication.quantity}
                                                            onChange={(e) => handleMedicationChange(index, "quantity", e.target.value)}
                                                            className="border px-2 h-[32px] rounded w-full focus:border-green-500 focus:ring-green-500 focus:outline-none"
                                                            min="1"
                                                        />
                                                    ) : (
                                                        <p className="text-gray-700">{medication.quantity || "N/A"}</p>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex flex-col gap-4 md:flex-row md:items-center">
                                                <div className="flex items-center w-full gap-4">
                                                    <div className="flex items-center gap-4 mb-2">
                                                        <label className="text-sm font-semibold text-nowrap">Allow Refills</label>
                                                        <div className="flex items-center gap-2">
                                                            <label className="relative inline-flex items-center cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    className="sr-only peer"
                                                                    checked={medication.refills > 0}
                                                                    onChange={(e) => {
                                                                        handleMedicationChange(
                                                                            index,
                                                                            "refills",
                                                                            e.target.checked ? 1 : 0
                                                                        );
                                                                    }}
                                                                    disabled={!isMedicationEditing}
                                                                />
                                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                                                            </label>
                                                            <span className={`text-sm font-medium ${medication.refills > 0 ? 'text-green-600' : 'text-gray-600'}`}>
                                                                {medication.refills > 0 ? 'Yes' : 'No'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {medication.refills > 0 && (
                                                        <div className="flex items-center w-full gap-2">
                                                            <label className="block text-sm font-semibold">No. of Refills</label>
                                                            {isMedicationEditing ? (
                                                                <input
                                                                    type="number"
                                                                    value={medication.refills}
                                                                    onChange={(e) => handleMedicationChange(index, "refills", e.target.value)}
                                                                    className="border px-2 h-[32px] rounded w-20 focus:border-green-500 focus:ring-green-500 focus:outline-none"
                                                                    min="1"
                                                                />
                                                            ) : (
                                                                <p className="text-gray-700">{medication.refills || "0"}</p>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Instructions */}
                                            <div className="sm:col-span-2">
                                                <label className="block text-sm font-semibold">Instructions {isMedicationEditing && <span className="text-xs text-gray-500">(Optional)</span>}</label>
                                                {isMedicationEditing ? (
                                                    <textarea
                                                        value={medication.instructions}
                                                        onChange={(e) => handleMedicationChange(index, "instructions", e.target.value)}
                                                        className="w-full h-20 px-2 py-1 border rounded focus:border-green-500 focus:ring-green-500 focus:outline-none"
                                                        placeholder="e.g., Take one tablet by mouth three times daily with food"
                                                    />
                                                ) : (
                                                    <p className="text-gray-700">{medication.instructions || "N/A"}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {isMedicationEditing && (
                            <div className="flex justify-end mt-4">
                                <button
                                    onClick={handleMedicationSubmit}
                                    type="button"
                                    disabled={isSaving.patient || isSaving.provider || isSaving.medication || isSaving.all}
                                    className={`bg-green-500 hover:opacity-90 text-white py-1 px-4 rounded-md shadow-sm text-sm ${(isSaving.patient || isSaving.provider || isSaving.medication || isSaving.all) ? 'opacity-50 cursor-not-allowed' : ''
                                        }`}
                                >
                                    {isSaving.medication ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Order Info Section */}
                    <div className="p-3 mb-6 bg-white rounded-lg shadow">
                        <h2 className="mb-2 text-lg font-semibold text-green-700">Order Info</h2>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold">Created By</label>
                                <p className="text-gray-700">
                                    {prescriptionDetail?.user ?
                                        <span className="font-extrabold text-transparent bg-gradient-to-r from-green-700 to-green-900 bg-clip-text">
                                            {`${prescriptionDetail.user.first_name} ${prescriptionDetail.user.last_name}`}
                                        </span> :
                                        "N/A"}
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold">Date Created</label>
                                <p className="text-gray-700">
                                    {prescriptionDetail?.created_at ?
                                        new Date(prescriptionDetail.created_at).toLocaleDateString(

                                        ) :
                                        "N/A"}
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold">Status</label>
                                <select
                                    value={editedStatus || 'pending'}
                                    onChange={handleStatusChange}
                                    disabled={isPickedUp}
                                    className={`w-full p-2 border rounded focus:border-green-500 focus:ring-green-500 focus:outline-none ${isPickedUp ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                >
                                    <option value="pending">Pending</option>
                                    <option value="preparing">Dispensing</option>
                                    <option value="is_ready">Ready</option>
                                    <option value="picked_up">Picked Up</option>
                                    <option value="cancelled">Cancelled</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold">
                                    {editedStatus === 'cancelled' ? 'Date Cancelled' :
                                        editedStatus === 'picked_up' ? 'Date Picked Up' :
                                            editedStatus === 'is_ready' ? 'Date Ready' :
                                                editedStatus === 'preparing' ? 'Date Started Preparing' :
                                                    'Date Issued'}
                                </label>
                                <p className="text-gray-700">
                                    {statusDate ?
                                        new Date(statusDate).toLocaleDateString(

                                        ) :
                                        "N/A"}
                                </p>
                            </div>
                        </div>

                        {/* Prescription Comments Section */}
                        <div className="mt-6">
                            <label className="block text-sm font-semibold">Notes</label>

                            <div className="p-4 border border-gray-300 rounded-md">
                                {/* Display existing comments */}
                                <div className="mb-4 text-gray-700 whitespace-pre-wrap">
                                    {prescriptionComment.split('\n\n').map((comment, index) => {
                                        const [datePart, ...contentParts] = comment.split(': ');
                                        const content = contentParts.join(': ');
                                        return (
                                            <div key={index} className="mb-2">
                                                <span className="font-semibold text-green-900">{datePart}</span>: {content}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Add new comment */}
                                <div className="pt-4 mt-4 border-t border-gray-200">
                                    <textarea
                                        value={newComment}
                                        onChange={(e) => setNewComment(e.target.value)}
                                        disabled={isPickedUp}
                                        className={`w-full px-3 py-2 focus:outline-none ${isPickedUp ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                        rows="3"
                                        placeholder="Enter your new comment here..."
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Save Button at the bottom */}
                    <div className="flex justify-end mt-6 mb-4">
                        <button
                            onClick={handleSaveAll}
                            type="button"
                            disabled={isPickedUp || isSaving.patient || isSaving.provider || isSaving.medication || isSaving.all}
                            className={`bg-green-500 hover:opacity-90 text-white py-2 px-6 rounded-md shadow-sm text-sm ${(isPickedUp || isSaving.patient || isSaving.provider || isSaving.medication || isSaving.all) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {isSaving.all ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ViewEditPrescription;